"use client";

import React, { useEffect, useState } from "react";
import {
  TbBuilding,
  TbEye,
  TbSwitchHorizontal,
  TbSettings,
  TbUser,
  TbScanPosition,
  TbCurrencyDollar,
  TbCheck,
  TbInfoCircle,
  TbX,
  TbChevronDown,
  TbChevronUp,
  TbHistory,
} from "react-icons/tb";
import { FiSend } from "react-icons/fi";
import axios from "axios";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../Components/ui/dropdown-menu";
import { Button } from "../../../../Components/ui/button";
import { toast } from "sonner";

import BentoBox from "./BentoBox";
import VehicleBentoBox from "./VehicleBentoBox";
import { VehiclePhotoCarousel } from "../../VehiclePhotosCarousel";

export default function LoadDetails({
  scrollContainerRef,
  selectedLoad,
  userRoleId,

  drivers = [],
  shippers = [],

  // status UI
  currentStatus = "",
  isDroppedOrLater = false,
  showStatusButton = false,
  showBOLButton = false,

  // driver display
  selectedDriverName = "No driver assigned",
  selectedDriverAvatar = "https://new.cajuu.app/storage/profile_pictures/profile_picture.png",

  // callbacks
  onOpenModalAction,
  onActionClick,
  onOpenTransferModal,
  onOpenDeleteDialog,
  onOpenNotesModal,
  onOpenViewGatePassModal,
  hasGatePasses,
  onEditVehicles,
  onOpenBOL,
  onOpenInvoice,
  onStatusUpdate,

  // factoring
  factoringInfo = null,
  discountedRate = 0,
  canRequestFactoring = false,
  onOpenFactoringModal,

  // cancel factoring
  canCancelFactoring = false,
  onOpenCancelFactoringDialog,
}) {
  const [isFactoringExpanded, setIsFactoringExpanded] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [isActivityLogsLoading, setIsActivityLogsLoading] = useState(false);
  const [activityLogsError, setActivityLogsError] = useState("");

  const formatActivityDate = (value) => {
    if (!value) return "Unknown date";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "Unknown date";
    }

    const datePart = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    const timePart = date
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      .replace(/^24:/, "00:");

    return `${datePart} ${timePart}`;
  };

  const getActivityHighlightClass = (modelType) => {
    const normalizedType = String(modelType || "").toLowerCase();

    if (normalizedType === "user") {
      return "font-semibold text-amber-200";
    }

    if (normalizedType === "driver") {
      return "font-semibold text-emerald-200";
    }

    if (normalizedType === "company") {
      return "font-semibold text-sky-200";
    }

    if (normalizedType === "status") {
      return "font-semibold text-blue-200";
    }

    if (normalizedType === "vehicle") {
      return "font-semibold text-violet-200";
    }

    return "font-semibold text-amber-200";
  };

  const removeModelIdsFromText = (text) => {
    return String(text || "").replace(/\s*\[\d+\]/g, "");
  };

  const renderActivityDescription = (description) => {
    if (!description) {
      return <span>No description available.</span>;
    }

    const text = String(description);

    const modelReferenceRegex =
      /\b(user|driver|company|status|vehicle)\s+\[\d+\]\s+(.+?)(?=(?:\s+(?:by|from|to|for|with|and|added|removed|uploaded|deleted|completed|updated|changed|requested|approved|rejected|assigned|reassigned|unassigned|created|generated|archived|restored)\b)|$)/gi;

    const nodes = [];
    let lastIndex = 0;
    let nodeKey = 0;

    text.replace(modelReferenceRegex, (match, modelType, modelValue, offset) => {
      const plainTextBefore = removeModelIdsFromText(text.slice(lastIndex, offset));

      if (plainTextBefore) {
        nodes.push(
          <React.Fragment key={`plain-${nodeKey++}`}>
            {plainTextBefore}
          </React.Fragment>
        );
      }

      const cleanModelValue = removeModelIdsFromText(modelValue).trim();

      nodes.push(
        <React.Fragment key={`model-${nodeKey++}`}>
          <span className="text-zinc-200">{modelType.toLowerCase()} </span>
          <span className={getActivityHighlightClass(modelType)}>
            {cleanModelValue}
          </span>
        </React.Fragment>
      );

      lastIndex = offset + match.length;

      return match;
    });

    const remainingText = removeModelIdsFromText(text.slice(lastIndex));

    if (remainingText) {
      nodes.push(
        <React.Fragment key={`plain-${nodeKey++}`}>
          {remainingText}
        </React.Fragment>
      );
    }

    if (nodes.length === 0) {
      return <span>{removeModelIdsFromText(text)}</span>;
    }

    return nodes;
  };

  useEffect(() => {
    if (!selectedLoad?.id) {
      setActivityLogs([]);
      setActivityLogsError("");
      return;
    }

    const abortController = new AbortController();

    const fetchActivityLogs = async () => {
      setIsActivityLogsLoading(true);
      setActivityLogsError("");

      try {
        const userToken = localStorage.getItem("token");
        const response = await axios.get(
          `https://new.cajuu.app/api/loadtransport/${selectedLoad.id}/activity-logs`,
          {
            headers: {
              Authorization: `Bearer ${userToken}`,
              "Screen-Name": "/Load",
            },
            signal: abortController.signal,
          }
        );

        const payload = response.data?.data;
        const logs = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];

        setActivityLogs(logs);
      } catch (error) {
        if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
          return;
        }

        console.error("Error fetching Load Transport activity logs", error);
        setActivityLogs([]);
        setActivityLogsError("Failed to load activity history.");
      } finally {
        if (!abortController.signal.aborted) {
          setIsActivityLogsLoading(false);
        }
      }
    };

    fetchActivityLogs();

    return () => abortController.abort();
  }, [selectedLoad?.id]);

  // Sempre iniciar collapsado ao trocar de load / status de factoring
  useEffect(() => {
    setIsFactoringExpanded(false);
  }, [selectedLoad?.id, factoringInfo?.status]);

  if (!selectedLoad) {
    return (
      <div
        ref={scrollContainerRef}
        className="flex-1 min-h-0 p-2 sm:p-4 md:p-6 bg-[#1c1b19] text-white overflow-y-auto overflow-x-hidden h-[70vh] lg:h-[90vh] rounded-lg shadow-md"
      >
        <p className="text-gray-500 italic">Select a load to see its details.</p>
      </div>
    );
  }

  const vehicles = selectedLoad.vehicles || selectedLoad.load_items || [];

  return (
    <div
      ref={scrollContainerRef}
      className="flex-1 min-h-0 p-2 sm:p-4 md:p-6 bg-[#1c1b19] text-white overflow-y-auto overflow-x-hidden h-[70vh] lg:h-[90vh] rounded-lg shadow-md"
    >
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 sm:mb-4 md:mb-6">
        <div className="flex items-center">
          <h2
            id="selectedLoad"
            className="text-xl sm:text-2xl font-bold mb-2 sm:mb-0 mr-2 scroll-mt-4"
          >
            Order ID: {selectedLoad?.order_id || "No Load Selected"}
          </h2>
          <span className="text-sm text-gray-400 mt-3">
            ID: {selectedLoad?.id || "No ID"}
          </span>
        </div>

        <div className="flex items-center mt-2 sm:mt-0 space-x-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!selectedLoad}
            onClick={() =>
              window.open(
                `/company/public/${selectedLoad.partner_company_id}`,
                "_blank"
              )
            }
            title="View Company Profile"
          >
            <TbBuilding className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            disabled={!selectedLoad}
            onClick={() => window.open(`/loads/public/${selectedLoad.id}`, "_blank")}
            title="View Public Page"
          >
            <TbEye className="h-4 w-4" />
          </Button>

          {["New", "Assigned", "Confirmed"].includes(
            selectedLoad?.load_status?.load_status_name
          ) &&
            [1, 4, 7].includes(userRoleId) && (
              <Button
                variant="outline"
                size="sm"
                disabled={!selectedLoad}
                onClick={() => onOpenTransferModal?.()}
                title="Transfer Load to Another Company"
              >
                <TbSwitchHorizontal className="h-4 w-4" />
              </Button>
            )}

          {[1, 2, 4, 7].includes(userRoleId) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={!selectedLoad}>
                  <FiSend className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onSelect={() => onActionClick?.("Send BOL by Email", selectedLoad)}
                >
                  Send BOL by Documents
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!selectedLoad}>
                <TbSettings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>

            <DropdownMenuContent>
              {[1, 2, 4, 7].includes(userRoleId) && (
                <DropdownMenuItem
                  onSelect={() => onActionClick?.("Gatepass", selectedLoad)}
                >
                  Gatepass
                </DropdownMenuItem>
              )}

              {[1, 4, 7].includes(userRoleId) && (
                <DropdownMenuItem
                  onSelect={() => onOpenDeleteDialog?.()}
                  className="text-red-600"
                >
                  Delete Load
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* DRIVER */}
        <BentoBox title="Driver" icon={TbUser}>
          <div className="space-y-4 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Name:</span>

                <span className="text-sm text-white flex items-center gap-2">
                  <img
                    src={selectedDriverAvatar}
                    alt={selectedDriverName}
                    className="w-6 h-6 rounded-full border-2 border-gray-300 object-cover"
                    onError={(e) => {
                      e.target.src = "/img/user-avatar.png";
                    }}
                  />
                  <span>{selectedDriverName}</span>
                </span>
              </div>

              <div className="flex justify-end mt-4">
                {[1, 4, 7].includes(userRoleId) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenModalAction?.("Reassign Driver")}
                    className="text-white border-gray-500 hover:bg-gray-700 transition-all"
                  >
                    Reassign Driver
                  </Button>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 mt-4">
              <div className="w-full sm:w-auto">
                <Button
                  size="sm"
                  onClick={() => {
                    if (selectedLoad.dispatch_sheet_pdf_path) {
                      window.open(
                        `https://new.cajuu.app/storage/${selectedLoad.dispatch_sheet_pdf_path}`,
                        "_blank",
                        "noopener,noreferrer"
                      );
                    } else {
                      toast.error("BOL path is not available");
                    }
                  }}
                  className="bg-yellow-600 text-white hover:bg-yellow-500 w-full"
                >
                  Dispatch Sheet PDF
                </Button>
              </div>

              <div className="w-full sm:w-auto">
                <Button
                  size="sm"
                  onClick={() => onOpenNotesModal?.()}
                  className="bg-yellow-600 text-white hover:bg-yellow-500 w-full"
                >
                  Notes
                </Button>
              </div>

              {typeof hasGatePasses === "function" && hasGatePasses() && (
                <div className="w-full sm:w-auto">
                  <Button
                    size="sm"
                    onClick={() => onOpenViewGatePassModal?.()}
                    className="bg-yellow-600 text-white hover:bg-yellow-500 w-full"
                  >
                    View Gate Pass
                  </Button>
                </div>
              )}
            </div>
          </div>
        </BentoBox>

        {/* VEHICLES */}
        <VehicleBentoBox
          vehicles={vehicles}
          invoicePdfPath={selectedLoad.invoice_pdf_path}
          onEditVehicles={onEditVehicles}
          onOpenBOL={onOpenBOL}
          onOpenInvoice={onOpenInvoice}
          isPickedUp={isDroppedOrLater}
          onStatusUpdate={onStatusUpdate}
          currentStatus={currentStatus}
          showStatusButton={showStatusButton}
          showBOLButton={showBOLButton}
          selectedLoad={selectedLoad}
          userRoleId={userRoleId}
        />

        {/* PICKUP */}
        <BentoBox title="Pickup" icon={TbScanPosition}>
          <div className="space-y-4 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Business:</span>
                <span className="text-sm text-white">
                  {selectedLoad.pickup_business_name || "Unknown Business"}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Location:</span>
                <span className="text-sm text-white">
                  {selectedLoad.pickup_location_type || "Unknown Location"}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Name:</span>
                <span className="text-sm text-white">
                  {selectedLoad.pickup_customer_name || "Unknown Name"}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">E-mail:</span>
                <span className="text-sm text-white">
                  {selectedLoad.pickup_customer_email || "Unknown E-mail"}
                </span>
              </div>

              <div className="flex flex-col space-y-1 col-span-2">
                <span className="text-sm text-white font-bold">Destination:</span>

                <span className="text-sm text-white flex items-center">
                  {selectedLoad.pickup_address || "Unknown Address"}
                  <button
                    className="ml-2 p-1 text-white hover:text-gray-300"
                    onClick={() => {
                      const address = `${selectedLoad.pickup_address || "Unknown Address"}, ${
                        selectedLoad.pickup_city || "Unknown City"
                      }, ${selectedLoad.pickup_state || "Unknown State"}, ${
                        selectedLoad.pickup_zipcode || "Unknown ZIP"
                      }`;
                      navigator.clipboard.writeText(address);
                      toast.success("Copied!");
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      className="w-5 h-5"
                    >
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                    </svg>
                  </button>
                </span>

                <span className="text-sm text-white">
                  {selectedLoad.pickup_city || "Unknown City"},{" "}
                  {selectedLoad.pickup_state || "Unknown State"},{" "}
                  {selectedLoad.pickup_zipcode || "Unknown ZIP"}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Phone:</span>
                <span className="text-sm text-white">
                  {selectedLoad.pickup_customer_phone || "Unknown Phone"}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Date:</span>
                <span className="text-sm text-white">
                  {selectedLoad.expected_pickup_date
                    ? new Date(selectedLoad.expected_pickup_date).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                        }
                      )
                    : "Unknown Date"}
                </span>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              {[1, 2, 4, 7].includes(userRoleId) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-white border-gray-500 hover:bg-gray-700 transition-all"
                  onClick={() => onOpenModalAction?.("Provide Pickup ETA")}
                >
                  Edit Pickup
                </Button>
              )}
            </div>
          </div>
        </BentoBox>

        {/* DELIVERY */}
        <BentoBox title="Delivery" icon={TbScanPosition}>
          <div className="space-y-4 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Business:</span>
                <span className="text-sm text-white">
                  {selectedLoad.delivery_business_name || "Unknown Business"}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Location:</span>
                <span className="text-sm text-white">
                  {selectedLoad.delivery_location_type || "Unknown Location"}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Name:</span>
                <span className="text-sm text-white">
                  {selectedLoad.delivery_customer_name || "Unknown Name"}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">E-mail:</span>
                <span className="text-sm text-white">
                  {selectedLoad.delivery_customer_email || "Unknown E-mail"}
                </span>
              </div>

              <div className="flex flex-col space-y-1 col-span-2">
                <span className="text-sm text-white font-bold">Destination:</span>

                <span className="text-sm text-white flex items-center">
                  {selectedLoad.delivery_address || "Unknown Address"}
                  <button
                    className="ml-2 p-1 text-white hover:text-gray-300"
                    onClick={() => {
                      const address = `${selectedLoad.delivery_address || "Unknown Address"}, ${
                        selectedLoad.delivery_city || "Unknown City"
                      }, ${selectedLoad.delivery_state || "Unknown State"}, ${
                        selectedLoad.delivery_zipcode || "Unknown ZIP"
                      }`;
                      navigator.clipboard.writeText(address);
                      toast.success("Copied!");
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                      className="w-5 h-5"
                    >
                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" />
                    </svg>
                  </button>
                </span>

                <span className="text-sm text-white">
                  {selectedLoad.delivery_city || "Unknown City"},{" "}
                  {selectedLoad.delivery_state || "Unknown State"},{" "}
                  {selectedLoad.delivery_zipcode || "Unknown ZIP"}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Phone:</span>
                <span className="text-sm text-white">
                  {selectedLoad.delivery_customer_phone || "Unknown Phone"}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Date:</span>
                <span className="text-sm text-white">
                  {selectedLoad.expected_delivery_date
                    ? new Date(selectedLoad.expected_dropoff_date).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                        }
                      )
                    : "Unknown Date"}
                </span>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              {[1, 2, 4, 7].includes(userRoleId) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-white border-gray-500 hover:bg-gray-700 transition-all"
                  onClick={() => onOpenModalAction?.("Provide Delivery ETA")}
                >
                  Edit Delivery
                </Button>
              )}
            </div>
          </div>
        </BentoBox>

        {/* PAYMENT */}
        <BentoBox title="Payment" icon={TbCurrencyDollar}>
          <div className="space-y-4 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Payment Rate:</span>
                <span className="text-sm text-white">${selectedLoad.payment_rate}</span>
              </div>
              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Payment Type:</span>
                <span className="text-sm text-white">{selectedLoad.payment_type}</span>
              </div>
              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Payment Method:</span>
                <span className="text-sm text-white">{selectedLoad.payment_method}</span>
              </div>
            </div>

            {factoringInfo &&
              (() => {
                const statusRaw = (factoringInfo.status || "").toString().toLowerCase();
                const isApproved = statusRaw === "approved";
                const isPending = statusRaw === "pending";
                const isCancelled = statusRaw === "cancelled";
                const isRejected = statusRaw === "rejected";

                const isKnown = isApproved || isPending || isCancelled || isRejected;

                const statusKey = isApproved
                  ? "approved"
                  : isPending
                  ? "pending"
                  : isRejected
                  ? "rejected"
                  : isCancelled
                  ? "cancelled"
                  : "unknown";

                const themeByStatus = {
                  approved: {
                    containerClass:
                      "mt-4 p-4 rounded-lg border bg-emerald-900/10 border-emerald-700/40",
                    iconWrapClass:
                      "w-9 h-9 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center",
                    titleClass: "text-emerald-200",
                    badgeClass:
                      "text-[11px] px-2 py-0.5 rounded-full border bg-emerald-500/10 border-emerald-500/30 text-emerald-200 uppercase tracking-wider whitespace-nowrap shrink-0",
                    icon: <TbCheck className="text-emerald-300" size={18} />,
                    title: "Factoring approved",
                    badge: "APPROVED",
                  },
                  pending: {
                    containerClass:
                      "mt-4 p-4 rounded-lg border bg-blue-500/10 border-blue-500/30",
                    iconWrapClass:
                      "w-9 h-9 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center",
                    titleClass: "text-blue-200",
                    badgeClass:
                      "text-[11px] px-2 py-0.5 rounded-full border bg-blue-500/10 border-blue-500/30 text-blue-200 uppercase tracking-wider whitespace-nowrap shrink-0",
                    icon: <TbInfoCircle className="text-blue-300" size={18} />,
                    title: "Factoring under review",
                    badge: "PENDING",
                  },
                  rejected: {
                    containerClass:
                      "mt-4 p-4 rounded-lg border bg-rose-500/10 border-rose-500/30",
                    iconWrapClass:
                      "w-9 h-9 rounded-full bg-rose-500/10 border border-rose-500/30 flex items-center justify-center",
                    titleClass: "text-rose-200",
                    badgeClass:
                      "text-[11px] px-2 py-0.5 rounded-full border bg-rose-500/10 border-rose-500/30 text-rose-200 uppercase whitespace-nowrap shrink-0",
                    icon: <TbX className="text-rose-300" size={18} />,
                    title: "Factoring rejected",
                    badge: "REJECTED",
                  },
                  cancelled: {
                    containerClass:
                      "mt-4 p-4 rounded-lg border bg-zinc-500/10 border-zinc-500/30",
                    iconWrapClass:
                      "w-9 h-9 rounded-full bg-zinc-500/10 border border-zinc-500/30 flex items-center justify-center",
                    titleClass: "text-zinc-200",
                    badgeClass:
                      "text-[11px] px-2 py-0.5 rounded-full border bg-zinc-500/10 border-zinc-500/30 text-zinc-200 uppercase tracking-wider whitespace-nowrap shrink-0",
                    icon: <TbX className="text-zinc-300" size={18} />,
                    title: "Factoring cancelled",
                    badge: "CANCELLED",
                  },
                  unknown: {
                    containerClass:
                      "mt-4 p-4 rounded-lg border bg-zinc-500/10 border-zinc-500/30",
                    iconWrapClass:
                      "w-9 h-9 rounded-full bg-zinc-500/10 border border-zinc-500/30 flex items-center justify-center",
                    titleClass: "text-zinc-200",
                    badgeClass:
                      "text-[11px] px-2 py-0.5 rounded-full border bg-zinc-500/10 border-zinc-500/30 text-zinc-200 uppercase tracking-wider whitespace-nowrap shrink-0",
                    icon: <TbInfoCircle className="text-zinc-300" size={18} />,
                    title: "Factoring status",
                    badge: (factoringInfo.status || "UNKNOWN").toString().toUpperCase(),
                  },
                };

                const theme = themeByStatus[statusKey] || themeByStatus.unknown;

                const metaDateRaw =
                  (isApproved && (factoringInfo.approved_at || factoringInfo.approvedAt)) ||
                  (isRejected && (factoringInfo.rejected_at || factoringInfo.rejectedAt)) ||
                  (isCancelled &&
                    (factoringInfo.cancelled_at ||
                      factoringInfo.canceled_at ||
                      factoringInfo.cancelledAt)) ||
                  factoringInfo.updated_at ||
                  factoringInfo.created_at;

                const metaDate = metaDateRaw
                  ? new Date(metaDateRaw).toLocaleString("en-US")
                  : "N/A";

                const metaLabel = isApproved
                  ? "Approved on"
                  : isPending
                  ? "Requested on"
                  : isRejected
                  ? "Reviewed on"
                  : isCancelled
                  ? "Cancelled on"
                  : "Updated on";

                const payoutTitle = isApproved
                  ? "Expected payout"
                  : isPending
                  ? "Expected payout"
                  : "Potential payout";

                const payoutAmountClass =
                  isApproved || isPending ? "text-emerald-200" : "text-zinc-200";

                const payoutBorderClass = isApproved
                  ? "border-emerald-700/30"
                  : isPending
                  ? "border-blue-500/20"
                  : isRejected
                  ? "border-rose-500/20"
                  : isCancelled
                  ? "border-zinc-500/20"
                  : "border-zinc-500/20";

                const amountValue = `$${Number(discountedRate || 0).toFixed(2)}`;

                return (
                  <div className={theme.containerClass}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className={theme.iconWrapClass}>{theme.icon}</div>

                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-nowrap min-w-0">
                            <div className={`text-sm font-semibold truncate ${theme.titleClass}`}>
                              {theme.title}
                            </div>
                            <span className={theme.badgeClass}>{theme.badge}</span>
                          </div>

                          <div
                            className={`mt-1 text-xs text-white/70 leading-snug ${
                              isFactoringExpanded ? "hidden" : ""
                            }`}
                          >
                            {metaLabel}{" "}
                            <span className="text-white font-semibold whitespace-nowrap">
                              {metaDate}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <div
                          className={`text-base font-bold whitespace-nowrap order-2 ${payoutAmountClass} ${
                            isFactoringExpanded ? "hidden" : ""
                          }`}
                        >
                          {amountValue}
                        </div>

                        <button
                          type="button"
                          onClick={() => setIsFactoringExpanded((v) => !v)}
                          className="w-9 h-9 rounded-lg border border-white/10 bg-black/20 hover:bg-black/30 flex items-center justify-center transition order-1"
                          aria-label={
                            isFactoringExpanded ? "Collapse factoring" : "Expand factoring"
                          }
                        >
                          {isFactoringExpanded ? (
                            <TbChevronUp className="text-white/80" size={18} />
                          ) : (
                            <TbChevronDown className="text-white/80" size={18} />
                          )}
                        </button>
                      </div>
                    </div>

                    {isFactoringExpanded && (
                      <div className="mt-3">
                        <div className="mt-1 text-xs text-white/70 leading-relaxed">
                          {isApproved ? (
                            <>
                              Approved on{" "}
                              <span className="text-white font-semibold">{metaDate}</span>.
                            </>
                          ) : isPending ? (
                            <>
                              Requested on{" "}
                              <span className="text-white font-semibold">{metaDate}</span>.{" "}
                              We&apos;re reviewing your request.
                            </>
                          ) : isRejected ? (
                            <>
                              Reviewed on{" "}
                              <span className="text-white font-semibold">{metaDate}</span>.{" "}
                              Your request was not approved.
                            </>
                          ) : isCancelled ? (
                            <>
                              Cancelled on{" "}
                              <span className="text-white font-semibold">{metaDate}</span>.{" "}
                              This request is no longer active.
                            </>
                          ) : (
                            <>
                              Updated on{" "}
                              <span className="text-white font-semibold">{metaDate}</span>.
                            </>
                          )}
                        </div>

                        <div
                          className={`mt-3 rounded-lg p-3 border bg-black/20 ${payoutBorderClass}`}
                        >
                          <div className="text-[11px] text-white/60 uppercase tracking-wider whitespace-nowrap shrink-0">
                            {payoutTitle}
                          </div>

                          <div className={`mt-1 text-lg font-bold ${payoutAmountClass}`}>
                            {amountValue}
                          </div>

                          <div className="mt-1 text-xs text-white/60 leading-relaxed">
                            {isApproved ? (
                              <>Funds are released within 2 hours after the 5% fee is deducted.</>
                            ) : isPending ? (
                              <>
                                Once approved, funds are released within 2 hours after the 5% fee
                                is deducted.
                              </>
                            ) : isRejected ? (
                              <>
                                No payout will be issued because the request was rejected. Review
                                documentation and try again if applicable.
                              </>
                            ) : isCancelled ? (
                              <>
                                No payout will be issued because the request was cancelled. You
                                can submit a new request if needed.
                              </>
                            ) : (
                              <>Payout depends on the factoring status.</>
                            )}
                          </div>
                        </div>

                        {isPending && (
                          <div className="mt-3 text-xs text-white/70">
                            <span className="text-blue-200 font-semibold">Tip:</span> Make sure
                            the vehicle photos and customer signature are completed to avoid delays.
                          </div>
                        )}

                        {isRejected && (
                          <div className="mt-3 text-xs text-white/70">
                            <span className="text-rose-200 font-semibold">Note:</span> If you believe
                            this is a mistake, contact your dispatcher/admin to review the load details.
                          </div>
                        )}

                        {isCancelled && (
                          <div className="mt-3 text-xs text-white/70">
                            <span className="text-amber-200 font-semibold">Note:</span> You can request
                            factoring again if the payment type is eligible and your driver has factoring enabled.
                          </div>
                        )}

                        {!isKnown && (
                          <div className="mt-3 text-xs text-white/70">
                            <span className="text-zinc-200 font-semibold">Status:</span>{" "}
                            {(factoringInfo.status || "unknown").toString()}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

            <div className="flex justify-end mt-4 gap-2">
              {canCancelFactoring && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-rose-400 border-rose-400 hover:bg-rose-500 hover:text-black transition-all"
                  onClick={() => onOpenCancelFactoringDialog?.()}
                >
                  Cancel Factoring
                </Button>
              )}

              {canRequestFactoring && !factoringInfo && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-amber-500 border-amber-500 hover:bg-amber-500 hover:text-black transition-all"
                  onClick={() => onOpenFactoringModal?.()}
                >
                  Request Factoring
                </Button>
              )}

              {[1, 2, 4, 7].includes(userRoleId) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-white border-gray-500 hover:bg-gray-700 transition-all"
                  onClick={() => onOpenModalAction?.("Edit Payment")}
                >
                  Edit Payment
                </Button>
              )}
            </div>
          </div>
        </BentoBox>

        {/* SHIPPER */}
        <BentoBox title="Shipper" icon={TbUser}>
          <div className="space-y-4 p-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Name:</span>
                <span className="text-sm text-white">
                  {(() => {
                    const shipperId = String(selectedLoad.shipper_id);
                    const shipper = shippers.find((s) => String(s.id) === shipperId);
                    return shipper
                      ? shipper.shipper_name || "Unknown Shipper"
                      : "Unknown Shipper";
                  })()}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Type:</span>
                <span className="text-sm text-white">
                  {(() => {
                    const shipperId = String(selectedLoad.shipper_id);
                    const shipper = shippers.find((s) => String(s.id) === shipperId);
                    return shipper ? shipper.type || "Unknown Type" : "Unknown Type";
                  })()}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Email:</span>
                <span className="text-sm text-white">
                  {(() => {
                    const shipperId = String(selectedLoad.shipper_id);
                    const shipper = shippers.find((s) => String(s.id) === shipperId);
                    return shipper
                      ? shipper.info_email || "Unknown Email"
                      : "Unknown Email";
                  })()}
                </span>
              </div>

              <div className="flex flex-col space-y-1">
                <span className="text-sm text-white font-bold">Address:</span>
                <span className="text-sm text-white">
                  {(() => {
                    const shipperId = String(selectedLoad.shipper_id);
                    const shipper = shippers.find((s) => String(s.id) === shipperId);
                    return shipper
                      ? `${shipper.address} - ${shipper.city} - ${shipper.state}` ||
                          "Unknown Address"
                      : "Unknown Address";
                  })()}
                </span>
              </div>

              <div className="flex flex-col space-y-7">
                <span className="text-sm text-white font-bold"> </span>
                <span className="text-sm text-gray-800">''</span>
              </div>
            </div>
          </div>
        </BentoBox>

        {/* PHOTOS */}
        {Array.isArray(vehicles) && vehicles.length > 0 && (
          <VehiclePhotoCarousel selectedLoad={selectedLoad} />
        )}

        {/* ACTIVITY HISTORY */}
        <BentoBox title="Activity History" icon={TbHistory} className="md:col-span-2">
          <div className="p-4">
            {isActivityLogsLoading ? (
              <p className="text-sm text-zinc-300 italic">Loading activity history...</p>
            ) : activityLogsError ? (
              <p className="text-sm text-rose-300">{activityLogsError}</p>
            ) : activityLogs.length === 0 ? (
              <p className="text-sm text-zinc-400 italic">No activity registered for this load yet.</p>
            ) : (
              <div className="max-h-80 overflow-y-auto pr-2">
                <ol className="relative border-s border-white/10 ml-2 space-y-4">
                  {activityLogs.map((activityLog) => (
                    <li key={activityLog.id} className="ms-4">
                      <div className="absolute w-2 h-2 bg-amber-400 rounded-full mt-2 -start-1" />
                      <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                        <p className="text-sm text-white leading-relaxed">
                          <span className="font-semibold text-amber-200">
                            {formatActivityDate(activityLog.happened_at)}
                          </span>
                          <span className="text-zinc-400"> - </span>
                          <span>{renderActivityDescription(activityLog.description)}</span>
                        </p>

                        {activityLog.event_type && (
                          <p className="mt-1 text-[11px] uppercase tracking-wider text-zinc-500">
                            {activityLog.event_type.replace(/_/g, " ")}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </BentoBox>
      </div>
    </div>
  );
}